import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  App,
  Upload,
  Select,
  Switch,
  Row,
  Col,
  InputNumber,
  Modal,
} from "antd";
import { CloseOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import API from "../../../services/api";
import { useCloudinaryUpload } from "../../../hooks/useCloudinaryUpload";
import { useState, useEffect } from "react";
import "./Ads.css";
import { useNavigate } from "react-router-dom";
import LocationField from "../../LocationField";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CATEGORY_OPTIONS = [
  "billboard",
  "vehicle",
  "walking",
  "wall",
  "shop_front",
  "bus_stop",
  "pole",
];

const VEHICLE_TYPES = ["cycle", "bike", "car", "auto", "bus", "truck"];
const PLACEMENT_POSITIONS = ["front", "back", "side", "top", "full_wrap"];
const SIZE_UNITS = ["ft", "m"];
const TRAFFIC_LEVELS = ["low", "medium", "high"];
const AUDIENCE_TYPES = ["pedestrian", "vehicle"];
const PRICING_UNITS = ["per_day", "per_week", "per_month"];
const SHIFT_TIMINGS = ["morning", "evening", "full_day"];

function Ads() {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [imagesError, setImagesError] = useState(false);

  const [openDropdown, setOpenDropdown] = useState(null);

  // ── CHANGED: replaced manual upload state with useCloudinaryUpload hook (matches CommercialSpace) ──
  const {
    uploadedThumb,
    uploadedImages,
    imageFileList,
    setImageFileList,
    uploading,
    uploadThumbnail,
    uploadImages,
    removeImage,
  } = useCloudinaryUpload("general", message);

  const getStepStyle = (step) => ({
    display: current === step ? "block" : "none",
    width: "100%",
  });

  const handleBack = () => {
    const values = form.getFieldsValue();

    // Check if any field has value
    const hasData = Object.values(values).some(
      (val) =>
        val !== undefined &&
        val !== null &&
        val !== "" &&
        !(Array.isArray(val) && val.length === 0)
    );

    // ✅ If no data → go back directly
    if (!hasData) {
      window.history.back();
      return;
    }

    // ⚠️ If user entered something → show confirm
    Modal.confirm({
      title: "Leave this page?",
      icon: <ExclamationCircleOutlined />,
      content: "All your unsaved changes will be lost if you go back.",
      okText: "Yes, go back",
      cancelText: "Stay",
      okButtonProps: { danger: true },
      onOk: () => {
        window.history.back();
      },
    });
  };

  const steps = [
    "Description",
    "Asset",
    "Location",
    "Visibility",
    "Pricing",
    "Contact",
    "Images",
  ];

  const stepFields = [
    ["title", "description"],
    ["category", "subType", "width", "height", "unit"],
    // ✅ CHANGED: replaced "city" with "locality" to match CommercialSpace
    ["address", "locality", "city", "state", "country", "pincode", "latitude", "longitude"],
    ["trafficLevel", "audienceType", "dailyExposureEstimate"],
    ["pricingUnit", "price", "minDuration", "negotiable"],
    ["contactName", "phone", "email"],
    ["thumbnail", "images"],
  ];

  // ── CHANGED: validateCurrentStep matches CommercialSpace pattern exactly ──
  const validateCurrentStep = async () => {
    const fieldsToValidate = stepFields[current];

    try {
      if (current === 6) {
        if (!uploadedThumb) {
          setThumbnailError(true);
          message.error("Please upload a thumbnail image");
          return false;
        }

        if (!uploadedImages || uploadedImages.length === 0) {
          setImagesError(true);
          message.error("Please upload at least one image");
          return false;
        }

        setThumbnailError(false);
        setImagesError(false);
      }

      // ✅ Only validate form fields if NOT step 6 (images)
      if (current !== 6) {
        // Conditional validation based on category
        const category = form.getFieldValue("category");
        let fieldsToValidateForStep = [...fieldsToValidate];

        if (current === 1) {
          if (category === "vehicle") {
            fieldsToValidateForStep = [...fieldsToValidateForStep, "vehicleType"];
          } else if (category === "walking") {
            fieldsToValidateForStep = [...fieldsToValidateForStep, "numberOfPeople", "workingHoursPerDay", "shiftTiming"];
          }

          // Add placementPosition and maxAdsAllowed only for specific categories
          if (["vehicle", "wall", "shop_front", "bus_stop", "pole"].includes(category)) {
            fieldsToValidateForStep = [...fieldsToValidateForStep, "placementPosition", "maxAdsAllowed"];
          }

          if (["billboard", "walking"].includes(category)) {
            fieldsToValidateForStep = [...fieldsToValidateForStep, "maxAdsAllowed"];
          }
        }

        if (current === 2) {
          if (category === "vehicle" || category === "walking") {
            fieldsToValidateForStep = [...fieldsToValidateForStep, "operatingArea"];
          }
        }

        await form.validateFields(fieldsToValidateForStep);
      }

      return true;

    } catch (err) {
      let errorMessage = "Please fill all required fields";

      if (err?.errorFields?.length > 0) {
        errorMessage = err.errorFields[0]?.errors?.[0] || "Please fill all required fields correctly";
      } else if (err?.message) {
        errorMessage = err.message;
      }

      message.destroy();
      message.error(errorMessage);
      return false;
    }
  };

  const next = async () => {
    const isValid = await validateCurrentStep();
    // 🔥 FORCE STOP
    if (!isValid) return;

    setCurrent(current + 1);
    window.scrollTo(0, 0);
  };

  const prev = () => {
    setCurrent(current - 1);
    window.scrollTo(0, 0);
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);

      // Process operating area
      const operatingArea = values.operatingArea
        ? values.operatingArea
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v.length >= 2)
        : [];

      const data = {
        title: values.title,
        description: values.description,
        category: values.category,
        subType: values.subType || "",
        assetDetails: {
          ...(values.category === "vehicle" && { vehicleType: values.vehicleType }),
          ...(["vehicle", "wall", "shop_front", "bus_stop", "pole"].includes(values.category) && {
            placementPosition: values.placementPosition,
            maxAdsAllowed: Number(values.maxAdsAllowed),
          }),
          size: {
            width: Number(values.width),
            height: Number(values.height),
            unit: values.unit,
          },
          ...(["billboard", "walking"].includes(values.category) && {
            maxAdsAllowed: Number(values.maxAdsAllowed),
          }),
        },
        ...(values.category === "walking" && {
          walkingDetails: {
            numberOfPeople: Number(values.numberOfPeople),
            workingHoursPerDay: Number(values.workingHoursPerDay),
            shiftTiming: values.shiftTiming,
          },
        }),
        location: {
          address: values.address,
          // ✅ ADDED: locality field sent to backend
          locality: values.locality.trim(),
          city: values.city,
          state: values.state,
          country: values.country,
          pincode: values.pincode,
          coordinates: {
            type: "Point",
            coordinates: [Number(values.longitude), Number(values.latitude)],
          },
          operatingArea: operatingArea,
        },
        visibility: {
          trafficLevel: values.trafficLevel,
          audienceType: values.audienceType,
          dailyExposureEstimate: Number(values.dailyExposureEstimate) || 0,
        },
        pricing: {
          unit: values.pricingUnit,
          price: Number(values.price),
          negotiable: !!values.negotiable,
          minDuration: Number(values.minDuration),
        },
        contact: {
          name: values.contactName,
          phone: values.phone,
          email: values.email,
        },
      };

      await API.post("/ads", {
        ...data,
        thumbnail: uploadedThumb?.url,
        images: uploadedImages.map(img => img.url),
      });

      navigate("/success", {
        state: { type: "ads" }
      });
      setThumbnailError(false);
      setImagesError(false);
    } catch (err) {
      message.error(
        err.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const watchCategory = Form.useWatch("category", form);


  useEffect(() => {
    const handleOutsideClick = (e) => {
      const clickedDropdown = e.target.closest(
        ".ads-custom-select"
      );

      if (!clickedDropdown) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  return (
    <div className="ads-page">
      <div className="ads-container">
        <Card className="ads-card">
          {/* Card Header - Back button on right side */}
          <div className="ads-card-header">
            <div className="ads-header-left" />
            <div className="ads-header-center">
              <Title className="ads-title">Create Advertisement</Title>
            </div>
            <div className="ads-header-right">
              <button className="ads-back-btn" onClick={handleBack} type="button">
                <CloseOutlined />
              </button>
            </div>
          </div>

          {/* Stepper */}
          <div className="ads-stepper">
            {steps.map((step, i) => (
              <div
                key={i}
                className={`ads-step ${current === i ? "active" : ""} ${current > i ? "done" : ""}`}
              >
                <div className="ads-step-line-left" />
                <div className="ads-step-inner">
                  <div className="ads-circle">
                    {current > i ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      i + 1
                    )}
                  </div>
                  <span className="ads-step-label">{step}</span>
                </div>
                <div className="ads-step-line-right" />
              </div>
            ))}
          </div>

          {/* Progress Bar */}
          <div className="ads-progress-bar">
            <div
              className="ads-progress-fill"
              style={{ width: `${(current / (steps.length - 1)) * 100}%` }}
            />
          </div>
          <div className="ads-progress-label">
            Step {current + 1} of {steps.length} — {steps[current]}
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onFinishFailed={({ errorFields }) => {
              const hasImageError = errorFields.some(
                (ef) => ef.name[0] === "thumbnail" || ef.name[0] === "images"
              );
              if (hasImageError) {
                const thumbError = errorFields.find((ef) => ef.name[0] === "thumbnail");
                const imgError = errorFields.find((ef) => ef.name[0] === "images");
                if (thumbError) setThumbnailError(true);
                if (imgError) setImagesError(true);
              }
            }}
            scrollToFirstError
            validateTrigger="onSubmit"
            className="ads-form"
          >
            {/* Step 0: Description */}
            <div style={getStepStyle(0)}>
              <Form.Item
                name="title"
                label={
                  <span className="ads-label-with-badge">
                    <span className="ads-required-star">*</span>
                    Advertisement Title
                  </span>
                }
                rules={[
                  { required: true, message: "Please enter a title for your advertisement" },
                  { min: 5, message: "Title must be at least 5 characters long" },
                  {
                    pattern: /^(?=.*[A-Za-z])[A-Za-z0-9][A-Za-z0-9\s.#&-]*$/,
                    message: "Title must include at least one letter and can contain numbers or symbols like # . & -",
                  },
                ]}
              >
                <Input placeholder="e.g. Premium Billboard on MG Road" />
              </Form.Item>

              <Form.Item
                name="description"
                label={
                  <span className="ads-label-with-badge">
                    <span className="ads-required-star">*</span>
                    Description
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please provide a description" },
                  { min: 20, message: "Description should be at least 20 characters" },
                ]}
                help={null}
                validateStatus="success"
              >
                <TextArea rows={5} placeholder="Describe the ad asset, location value, audience reach, and unique selling points..." />
              </Form.Item>
            </div>

            {/* Step 1: Asset */}
            <div style={getStepStyle(1)}>
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="category"
                    label={
                      <span className="ads-label-with-badge">
                        <span className="ads-required-star">*</span>
                        Category
                      </span>
                    }
                    required
                    rules={[{ required: true, message: "Please select a category" }]}
                  >
                    <div
                      className={`ads-custom-select ${openDropdown === "category"
                        ? "active"
                        : ""
                        }`}
                    >
                      <div
                        className="ads-custom-select-trigger"
                        onClick={() =>
                          setOpenDropdown(
                            openDropdown === "category"
                              ? null
                              : "category"
                          )
                        }
                      >
                        {form.getFieldValue("category")
                          ? form
                            .getFieldValue("category")
                            .split("_")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() +
                                word.slice(1)
                            )
                            .join(" ")
                          : "Select category"}

                        <span className="ads-custom-arrow">
                          ⌄
                        </span>
                      </div>

                      {openDropdown === "category" && (
                        <div className="ads-custom-dropdown">
                          {CATEGORY_OPTIONS.map((item) => (
                            <div
                              key={item}
                              className="ads-custom-dropdown-item"
                              onClick={() => {
                                form.setFieldValue(
                                  "category",
                                  item
                                );

                                setOpenDropdown(null);
                              }}
                            >
                              {item
                                .split("_")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1)
                                )
                                .join(" ")}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="subType"
                    label="Sub Type"
                    rules={[
                      { min: 2, message: "Sub type must be at least 2 characters" },
                      { max: 100, message: "Maximum 100 characters" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <Input placeholder="Optional subtype (e.g. Digital, Static)" />
                  </Form.Item>
                </Col>
              </Row>

              {/* Vehicle Type - Only for vehicle category */}
              {watchCategory === "vehicle" && (
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="vehicleType"
                      label={
                        <span className="ads-label-with-badge">
                          <span className="ads-required-star">*</span>
                          Vehicle Type
                        </span>
                      }
                      required
                      rules={[{ required: true, message: "Please select vehicle type" }]}
                      help={null}
                      validateStatus="success"
                    >
                      <div
                        className={`ads-custom-select ${openDropdown === "vehicleType"
                          ? "active"
                          : ""
                          }`}
                      >
                        <div
                          className="ads-custom-select-trigger"
                          onClick={() =>
                            setOpenDropdown(
                              openDropdown === "vehicleType"
                                ? null
                                : "vehicleType"
                            )
                          }
                        >
                          {form.getFieldValue("vehicleType")
                            ? form
                              .getFieldValue("vehicleType")
                              .charAt(0)
                              .toUpperCase() +
                            form
                              .getFieldValue("vehicleType")
                              .slice(1)
                            : "Select vehicle type"}

                          <span className="ads-custom-arrow">
                            ⌄
                          </span>
                        </div>

                        {openDropdown === "vehicleType" && (
                          <div className="ads-custom-dropdown">
                            {VEHICLE_TYPES.map((item) => (
                              <div
                                key={item}
                                className="ads-custom-dropdown-item"
                                onClick={() => {
                                  form.setFieldValue(
                                    "vehicleType",
                                    item
                                  );

                                  setOpenDropdown(null);
                                }}
                              >
                                {item.charAt(0).toUpperCase() +
                                  item.slice(1)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {["vehicle", "wall", "shop_front", "bus_stop", "pole"].includes(watchCategory) && (
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="placementPosition"
                      label={
                        <span className="ads-label-with-badge">
                          <span className="ads-required-star">*</span>
                          Placement Position
                        </span>
                      }
                      required
                      rules={[{ required: true, message: "Please select placement position" }]}
                    >
                      <div
                        className={`ads-custom-select ${openDropdown === "placementPosition"
                          ? "active"
                          : ""
                          }`}
                      >
                        <div
                          className="ads-custom-select-trigger"
                          onClick={() =>
                            setOpenDropdown(
                              openDropdown ===
                                "placementPosition"
                                ? null
                                : "placementPosition"
                            )
                          }
                        >
                          {form.getFieldValue(
                            "placementPosition"
                          )
                            ? form
                              .getFieldValue(
                                "placementPosition"
                              )
                              .split("_")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() +
                                  word.slice(1)
                              )
                              .join(" ")
                            : "Select placement"}

                          <span className="ads-custom-arrow">
                            ⌄
                          </span>
                        </div>

                        {openDropdown ===
                          "placementPosition" && (
                            <div className="ads-custom-dropdown">
                              {PLACEMENT_POSITIONS.map(
                                (item) => (
                                  <div
                                    key={item}
                                    className="ads-custom-dropdown-item"
                                    onClick={() => {
                                      form.setFieldValue(
                                        "placementPosition",
                                        item
                                      );

                                      setOpenDropdown(null);
                                    }}
                                  >
                                    {item
                                      .split("_")
                                      .map(
                                        (word) =>
                                          word.charAt(0).toUpperCase() +
                                          word.slice(1)
                                      )
                                      .join(" ")}
                                  </div>
                                )
                              )}
                            </div>
                          )}
                      </div>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="maxAdsAllowed"
                      label={
                        <span className="ads-label-with-badge">
                          <span className="ads-required-star">*</span>
                          Max Ads Allowed
                        </span>
                      }
                      required
                      rules={[
                        { required: true, message: "Please enter max ads allowed" },
                        { type: "number", min: 1, message: "Must be at least 1" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 10" min={1} />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {["billboard", "walking"].includes(watchCategory) && (
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="maxAdsAllowed"
                      label={
                        <span className="ads-label-with-badge">
                          <span className="ads-required-star">*</span>
                          Max Ads Allowed
                        </span>
                      }
                      required
                      rules={[
                        { required: true, message: "Please enter max ads allowed" },
                        { type: "number", min: 1, message: "Must be at least 1" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 10" min={1} />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="width"
                    label={
                      <span className="ads-label-with-badge">
                        <span className="ads-required-star">*</span>
                        Width
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter width" },
                      { type: "number", min: 0.1, message: "Width must be greater than 0" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="Width" min={0.1} step={0.1} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8}>
                  <Form.Item
                    name="height"
                    label={
                      <span className="ads-label-with-badge">
                        <span className="ads-required-star">*</span>
                        Height
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter height" },
                      { type: "number", min: 0.1, message: "Height must be greater than 0" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="Height" min={0.1} step={0.1} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8}>
                  <Form.Item
                    name="unit"
                    label={
                      <span className="ads-label-with-badge">
                        <span className="ads-required-star">*</span>
                        Size Unit
                      </span>
                    }
                    required
                    rules={[{ required: true, message: "Please select size unit" }]}
                    help={null}
                    validateStatus="success"
                  >
                    <div
                      className={`ads-custom-select ${openDropdown === "unit"
                        ? "active"
                        : ""
                        }`}
                    >
                      <div
                        className="ads-custom-select-trigger"
                        onClick={() =>
                          setOpenDropdown(
                            openDropdown === "unit"
                              ? null
                              : "unit"
                          )
                        }
                      >
                        {form.getFieldValue("unit")
                          ? form.getFieldValue("unit") ===
                            "ft"
                            ? "Feet"
                            : "Meters"
                          : "Select unit"}

                        <span className="ads-custom-arrow">
                          ⌄
                        </span>
                      </div>

                      {openDropdown === "unit" && (
                        <div className="ads-custom-dropdown">
                          {SIZE_UNITS.map((item) => (
                            <div
                              key={item}
                              className="ads-custom-dropdown-item"
                              onClick={() => {
                                form.setFieldValue(
                                  "unit",
                                  item
                                );

                                setOpenDropdown(null);
                              }}
                            >
                              {item === "ft"
                                ? "Feet"
                                : "Meters"}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Form.Item>
                </Col>
              </Row>

              {/* Walking Details - Only for walking category */}
              {watchCategory === "walking" && (
                <>
                  <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="numberOfPeople"
                        label={
                          <span className="ads-label-with-badge">
                            <span className="ads-required-star">*</span>
                            Number of People
                          </span>
                        }
                        required
                        rules={[
                          { required: true, message: "Please enter number of people" },
                          { type: "number", min: 1, message: "Must be at least 1" },
                        ]}
                        help={null}
                        validateStatus="success"
                      >
                        <InputNumber style={{ width: "100%" }} placeholder="e.g. 5" min={1} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="workingHoursPerDay"
                        label={
                          <span className="ads-label-with-badge">
                            <span className="ads-required-star">*</span>
                            Working Hours Per Day
                          </span>
                        }
                        required
                        rules={[
                          { required: true, message: "Please enter working hours" },
                          { type: "number", min: 1, max: 24, message: "Must be between 1 and 24" },
                        ]}
                        help={null}
                        validateStatus="success"
                      >
                        <InputNumber style={{ width: "100%" }} placeholder="e.g. 8" min={1} max={24} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="shiftTiming"
                        label={
                          <span className="ads-label-with-badge">
                            <span className="ads-required-star">*</span>
                            Shift Timing
                          </span>
                        }
                        required
                        rules={[{ required: true, message: "Please select shift timing" }]}
                        help={null}
                        validateStatus="success"
                      >
                        <div
                          className={`ads-custom-select ${openDropdown === "shiftTiming"
                            ? "active"
                            : ""
                            }`}
                        >
                          <div
                            className="ads-custom-select-trigger"
                            onClick={() =>
                              setOpenDropdown(
                                openDropdown ===
                                  "shiftTiming"
                                  ? null
                                  : "shiftTiming"
                              )
                            }
                          >
                            {form.getFieldValue(
                              "shiftTiming"
                            )
                              ? form
                                .getFieldValue(
                                  "shiftTiming"
                                )
                                .split("_")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1)
                                )
                                .join(" ")
                              : "Select shift timing"}

                            <span className="ads-custom-arrow">
                              ⌄
                            </span>
                          </div>

                          {openDropdown ===
                            "shiftTiming" && (
                              <div className="ads-custom-dropdown">
                                {SHIFT_TIMINGS.map((item) => (
                                  <div
                                    key={item}
                                    className="ads-custom-dropdown-item"
                                    onClick={() => {
                                      form.setFieldValue(
                                        "shiftTiming",
                                        item
                                      );

                                      setOpenDropdown(null);
                                    }}
                                  >
                                    {item
                                      .split("_")
                                      .map(
                                        (word) =>
                                          word.charAt(0).toUpperCase() +
                                          word.slice(1)
                                      )
                                      .join(" ")}
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
            </div>

            {/* Step 2: Location */}
            <div style={getStepStyle(2)}>
              <Form.Item
                name="address"
                label={
                  <span className="ads-label-with-badge">
                    <span className="ads-required-star">*</span>
                    Address
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter the Address" },
                  { min: 5, message: "Address must be at least 5 characters" },
                ]}
                help={null}
                validateStatus="success"
              >
                <Input placeholder="e.g. #45, MG Road, Near City Center Mall" />
              </Form.Item>

              {/* ✅ ADDED: Locality field using LocationField — same as CommercialSpace */}
              <Form.Item
                name="locality"
                label={
                  <span className="ads-label-with-badge">
                    <span className="ads-required-star">*</span>
                    Locality
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter locality" },
                  { min: 2, message: "Locality must be at least 2 characters" },
                  { max: 100, message: "Locality cannot exceed 100 characters" },
                  {
                    pattern: /^(?=.*[A-Za-z])[A-Za-z0-9\s.,#&()-]+$/,
                    message:
                      "Locality must contain at least one letter and can include numbers and symbols like . , # & ( ) -",
                  },
                ]}
              >
                <LocationField
                  form={form}
                  message={message}
                  setAutoDetecting={setAutoDetecting}
                />
              </Form.Item>

              {/* ✅ City field kept separately (populated by LocationField or manually entered) */}
              <Form.Item
                name="city"
                label={
                  <span className="ads-label-with-badge">
                    <span className="ads-required-star">*</span>
                    City
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter the city" },
                  { pattern: /^[A-Za-z\s.-]+$/, message: "City name should contain only letters" },
                ]}
                help={null}
                validateStatus="success"
              >
                <Input placeholder="e.g. Bengaluru" />
              </Form.Item>

              {/* Operating Area - Only for vehicle and walking categories */}
              {(watchCategory === "vehicle" || watchCategory === "walking") && (
                <Form.Item
                  name="operatingArea"
                  label={
                    <span className="ads-label-with-badge">
                      <span className="ads-required-star">*</span>
                      Operating Area
                    </span>
                  }
                  extra="Separate each area with a comma — e.g. Indiranagar, Koramangala, Whitefield"
                  required
                  rules={[
                    { required: true, message: "Please enter operating area" },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.reject("Please enter operating area");
                        const areas = value.split(",").map(a => a.trim()).filter(Boolean);
                        if (areas.length === 0) return Promise.reject("Please enter valid areas");
                        if (areas.some(a => a.length < 2)) return Promise.reject("Each area must be at least 2 characters");
                        return Promise.resolve();
                      },
                    },
                  ]}
                  help={null}
                  validateStatus="success"
                >
                  <Input placeholder="e.g. Indiranagar, Koramangala, Whitefield" />
                </Form.Item>
              )}

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="state"
                    label={
                      <span className="ads-label-with-badge">
                        <span className="ads-required-star">*</span>
                        State
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter the State" },
                      { pattern: /^[A-Za-z\s.-]+$/, message: "State name should contain only letters" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <Input placeholder="e.g. Karnataka" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="country"
                    label={
                      <span className="ads-label-with-badge">
                        <span className="ads-required-star">*</span>
                        Country
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter the country" },
                      { pattern: /^[A-Za-z\s.-]+$/, message: "Country name should contain only letters" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <Input placeholder="e.g. India" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="pincode"
                label={
                  <span className="ads-label-with-badge">
                    <span className="ads-required-star">*</span>
                    Pincode
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter the pincode" },
                  { pattern: /^[1-9][0-9]{5}$/, message: "Enter a valid 6-digit pincode" },
                ]}
                help={null}
                validateStatus="success"
              >
                <Input maxLength={6} placeholder="e.g. 560038" style={{ maxWidth: 200 }} />
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="latitude"
                    label={
                      <span className="ads-label-with-badge">
                        <span className="ads-required-star">*</span>
                        Latitude
                      </span>
                    }
                    rules={[
                      { required: true, message: "Please enter latitude" },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.reject("Latitude is required");
                          const str = value.toString();
                          if ((str.match(/\./g) || []).length > 1)
                            return Promise.reject("Only one decimal point allowed");
                          const parts = str.split(".");
                          if (parts[1] && parts[1].length > 6)
                            return Promise.reject("Max 6 digits allowed after decimal");
                          if (typeof value !== "number" || Number.isNaN(value))
                            return Promise.reject("Enter a valid latitude");
                          if (value < -90 || value > 90)
                            return Promise.reject("Latitude must be between -90 and 90");
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={-90}
                      max={90}
                      step={0.000001}
                      placeholder="e.g. 12.971891"
                      onBlur={() => {
                        const val = form.getFieldValue("latitude");
                        if (typeof val === "number") {
                          form.setFieldsValue({ latitude: Number(val.toFixed(6)) });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="longitude"
                    label={
                      <span className="ads-label-with-badge">
                        <span className="ads-required-star">*</span>
                        Longitude
                      </span>
                    }
                    rules={[
                      { required: true, message: "Please enter longitude" },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.reject("Longitude is required");
                          const str = value.toString();
                          if ((str.match(/\./g) || []).length > 1)
                            return Promise.reject("Only one decimal point allowed");
                          const parts = str.split(".");
                          if (parts[1] && parts[1].length > 6)
                            return Promise.reject("Max 6 digits allowed after decimal");
                          if (typeof value !== "number" || Number.isNaN(value))
                            return Promise.reject("Enter a valid longitude");
                          if (value < -180 || value > 180)
                            return Promise.reject("Longitude must be between -180 and 180");
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={-180}
                      max={180}
                      step={0.000001}
                      placeholder="e.g. 77.594563"
                      onBlur={() => {
                        const val = form.getFieldValue("longitude");
                        if (typeof val === "number") {
                          form.setFieldsValue({ longitude: Number(val.toFixed(6)) });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Step 3: Visibility */}
            <div style={getStepStyle(3)}>
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="trafficLevel"
                    label={
                      <span className="ads-label-with-badge">
                        <span className="ads-required-star">*</span>
                        Traffic Level
                      </span>
                    }
                    rules={[{ required: true, message: "Please select traffic level" }]}
                    help={null}
                    validateStatus="success"
                  >
                    <div
                      className={`ads-custom-select ${openDropdown === "trafficLevel"
                        ? "active"
                        : ""
                        }`}
                    >
                      <div
                        className="ads-custom-select-trigger"
                        onClick={() =>
                          setOpenDropdown(
                            openDropdown ===
                              "trafficLevel"
                              ? null
                              : "trafficLevel"
                          )
                        }
                      >
                        {form.getFieldValue(
                          "trafficLevel"
                        )
                          ? form
                            .getFieldValue(
                              "trafficLevel"
                            )
                            .charAt(0)
                            .toUpperCase() +
                          form
                            .getFieldValue(
                              "trafficLevel"
                            )
                            .slice(1)
                          : "Select traffic level"}

                        <span className="ads-custom-arrow">
                          ⌄
                        </span>
                      </div>

                      {openDropdown ===
                        "trafficLevel" && (
                          <div className="ads-custom-dropdown">
                            {TRAFFIC_LEVELS.map((item) => (
                              <div
                                key={item}
                                className="ads-custom-dropdown-item"
                                onClick={() => {
                                  form.setFieldValue(
                                    "trafficLevel",
                                    item
                                  );

                                  setOpenDropdown(null);
                                }}
                              >
                                {item.charAt(0).toUpperCase() +
                                  item.slice(1)}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="audienceType"
                    label={
                      <span className="ads-label-with-badge">
                        <span className="ads-required-star">*</span>
                        Audience Type
                      </span>
                    }
                    rules={[
                      {
                        required: true,
                        message: "Please select audience type",
                      },
                    ]}
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      showSearch
                      placeholder="Search audience type"
                      className="ads-multi-select"
                      optionFilterProp="label"
                      maxTagCount="responsive"
                      classNames={{ popup: { root: "ads-multi-dropdown" } }}

                      placement="bottomLeft"
                      getPopupContainer={(trigger) => trigger.parentNode}
                      popupMatchSelectWidth={true}


                      options={AUDIENCE_TYPES.map((item) => ({
                        value: item,
                        label:
                          item.charAt(0).toUpperCase() +
                          item.slice(1),
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="dailyExposureEstimate"
                label="Daily Exposure Estimate"
                rules={[
                  { type: "number", min: 0, message: "Must be 0 or greater" },
                ]}
                help={null}
                validateStatus="success"
              >
                <InputNumber style={{ width: "100%" }} placeholder="e.g. 5000" min={0} />
              </Form.Item>
            </div>

            {/* Step 4: Pricing */}
            <div style={getStepStyle(4)}>
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="pricingUnit"
                    label={
                      <span className="ads-label-with-badge">
                        <span className="ads-required-star">*</span>
                        Pricing Unit
                      </span>
                    }
                    rules={[{ required: true, message: "Please select pricing unit" }]}
                    help={null}
                    validateStatus="success"
                  >
                    <div
                      className={`ads-custom-select ${openDropdown === "pricingUnit"
                        ? "active"
                        : ""
                        }`}
                    >
                      <div
                        className="ads-custom-select-trigger"
                        onClick={() =>
                          setOpenDropdown(
                            openDropdown ===
                              "pricingUnit"
                              ? null
                              : "pricingUnit"
                          )
                        }
                      >
                        {form.getFieldValue(
                          "pricingUnit"
                        )
                          ? form
                            .getFieldValue(
                              "pricingUnit"
                            )
                            .split("_")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() +
                                word.slice(1)
                            )
                            .join(" ")
                          : "Select pricing unit"}

                        <span className="ads-custom-arrow">
                          ⌄
                        </span>
                      </div>

                      {openDropdown ===
                        "pricingUnit" && (
                          <div className="ads-custom-dropdown">
                            {PRICING_UNITS.map((item) => (
                              <div
                                key={item}
                                className="ads-custom-dropdown-item"
                                onClick={() => {
                                  form.setFieldValue(
                                    "pricingUnit",
                                    item
                                  );

                                  setOpenDropdown(null);
                                }}
                              >
                                {item
                                  .split("_")
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1)
                                  )
                                  .join(" ")}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="price"
                    label={
                      <span className="ads-label-with-badge">
                        <span className="ads-required-star">*</span>
                        Price (₹)
                      </span>
                    }
                    rules={[
                      { required: true, message: "Please enter the price" },
                      { type: "number", min: 1, message: "Price must be greater than 0" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="e.g. 5000" min={1} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="minDuration"
                    label={
                      <span className="ads-label-with-badge">
                        <span className="ads-required-star">*</span>
                        Minimum Duration (days)
                      </span>
                    }
                    rules={[
                      { required: true, message: "Please enter minimum duration" },
                      { type: "number", min: 1, message: "Duration must be at least 1 day" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="e.g. 30" min={1} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Negotiable" className="ads-switch-item">
                <Form.Item name="negotiable" valuePropName="checked" noStyle help={null}>
                  <Switch />
                </Form.Item>
                <span className="ads-switch-label">Price is open to negotiation</span>
              </Form.Item>
            </div>

            {/* Step 5: Contact */}
            <div style={getStepStyle(5)}>
              <Form.Item
                name="contactName"
                label={
                  <span className="ads-label-with-badge">
                    <span className="ads-required-star">*</span>
                    Contact Person Name
                  </span>
                }
                rules={[
                  { required: true, message: "Please enter contact name" },
                  { pattern: /^[A-Za-z\s.-]+$/, message: "Name can only contain letters and basic characters" },
                ]}
                help={null}
                validateStatus="success"
              >
                <Input placeholder="e.g. Rajesh Kumar" />
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="phone"
                    label={
                      <span className="ads-label-with-badge">
                        <span className="ads-required-star">*</span>
                        Phone Number
                      </span>
                    }
                    rules={[
                      { required: true, message: "Please enter a phone number" },
                      { pattern: /^[6-9]\d{9}$/, message: "Enter a valid 10-digit Indian phone number" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <Input maxLength={10} placeholder="e.g. 9876543210" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="email"
                    label={
                      <span className="ads-label-with-badge">
                        <span className="ads-required-star">*</span>
                        Email Address
                      </span>
                    }
                    rules={[
                      { required: true, message: "Please enter your email address" },
                      { type: "email", message: "Enter a valid email address" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <Input placeholder="e.g. contact@company.com" />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Step 6: Images — UPGRADED to match CommercialSpace exactly */}
            <div style={getStepStyle(6)}>
              <Form.Item
                required
                name="thumbnail"
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList}
                label={
                  <span className="ads-label-with-badge">
                    <span className="ads-required-star">*</span>
                    Thumbnail
                    <span className="ads-badge-primary">Main Image</span>
                  </span>
                }
                rules={[
                  { required: true, message: "Please upload a thumbnail image" },
                  {
                    validator: (_, value) => {
                      if (!value || value.length === 0) {
                        return Promise.reject("Please upload a thumbnail image");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                validateStatus={thumbnailError ? "error" : ""}
                help={thumbnailError ? "Please upload a thumbnail image" : ""}
              >
                <Upload.Dragger
                  maxCount={1}
                  accept="image/png,image/jpeg"
                  className="ads-upload"
                  beforeUpload={(file) => {
                    const isValidType = ["image/png", "image/jpeg"].includes(file.type);

                    if (!isValidType) {
                      message.error("Only PNG, JPEG files are allowed");
                      return Upload.LIST_IGNORE;
                    }

                    uploadThumbnail(file);
                    return false;
                  }}
                  onChange={() => {
                    setThumbnailError(false);
                  }}
                >
                  <div className="ads-upload-inner">
                    <p className="ads-upload-title">Upload Thumbnail</p>
                    <p className="ads-upload-hint">This is the first image users see · PNG, JPEG</p>
                  </div>
                </Upload.Dragger>
              </Form.Item>

              <Form.Item
                required
                name="images"
                label={
                  <span className="ads-label-with-badge">
                    <span className="ads-required-star">*</span>
                    Advertisement Images
                    <span className="ads-badge-secondary">Up to 5</span>
                  </span>
                }
                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      if (!value || value.length === 0) {
                        return Promise.reject("Please upload at least one image");
                      }
                      if (value.length > 5) {
                        return Promise.reject("Maximum 5 images allowed");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                validateStatus={imagesError ? "error" : ""}
                help={imagesError ? "Please upload at least one image" : ""}
              >
                <Upload.Dragger
                  fileList={imageFileList}
                  multiple
                  accept="image/png,image/jpeg"
                  className="ads-upload"

                  beforeUpload={(file) => {

                    const isValidType = ["image/png", "image/jpeg"].includes(file.type);

                    if (!isValidType) {
                      message.error("Only PNG and JPEG files are allowed");
                      return Upload.LIST_IGNORE;
                    }

                    // max 5 validation
                    if (imageFileList.length >= 5) {
                      message.error("Maximum 5 images allowed");
                      return Upload.LIST_IGNORE;
                    }

                    uploadImages([file]);

                    setImagesError(false);

                    return false;
                  }}

                  onRemove={(file) => {
                    removeImage(file.public_id);

                    const updatedList = imageFileList.filter(
                      (img) => img.public_id !== file.public_id
                    );

                    setImageFileList(updatedList);

                    form.setFieldsValue({
                      images: updatedList,
                    });
                  }}
                >
                  {imageFileList.length >= 5 ? null : (
                    <div className="ads-upload-inner">

                      <p className="ads-upload-title">
                        Upload Advertisement Images
                      </p>

                      <p className="ads-upload-hint">
                        Drag & drop images here or click to browse
                      </p>

                      <p className="ads-upload-hint">
                        PNG, JPEG · Maximum 5 images
                      </p>

                    </div>
                  )}
                </Upload.Dragger>
              </Form.Item>
            </div>

            {/* Navigation Buttons */}
            <div className="ads-btns">
              {current > 0 && (
                <Button
                  onClick={prev}
                  className="ads-btn-prev"
                  disabled={autoDetecting}
                >
                  ← Previous
                </Button>
              )}
              <div className="ads-btns-right">
                {current < steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={next}
                    className="ads-btn-next"
                    disabled={autoDetecting}
                  >
                    Next →
                  </Button>
                )}
                {current === steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={async () => {
                      const isValid = await validateCurrentStep();
                      if (!isValid) return;
                      form.submit();
                    }}
                    loading={loading}
                    disabled={loading}
                  >
                    Submit Listing
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
}

export default Ads;